import { AnyAction } from 'redux';
import { AxiosPromise, AxiosResponse } from 'axios';
import { ThunkAction } from 'redux-thunk';
import { ServerResponse } from 'data/types';
import { RootState } from 'data/root.reducers';

export const API_PATH_SECURITY_POLICY = '/security-policy';

export const GET_SECURITY_POLICY = 'GET_SECURITY_POLICY';
export const GET_SECURITY_POLICY_PENDING = 'GET_SECURITY_POLICY_PENDING';
export const GET_SECURITY_POLICY_FULFILLED = 'GET_SECURITY_POLICY_FULFILLED';
export const GET_SECURITY_POLICY_REJECTED = 'GET_SECURITY_POLICY_REJECTED';

export const UPDATE_SECURITY_POLICY = 'UPDATE_SECURITY_POLICY';
export const UPDATE_SECURITY_POLICY_PENDING = 'UPDATE_SECURITY_POLICY_PENDING';
export const UPDATE_SECURITY_POLICY_FULFILLED = 'UPDATE_SECURITY_POLICY_FULFILLED';
export const UPDATE_SECURITY_POLICY_REJECTED = 'UPDATE_SECURITY_POLICY_REJECTED';

export const SECURITY_POLICY_LOADING = 'SECURITY_POLICY_LOADING';
export const GO_TO_SECURITY_POLICY_EDIT = 'GO_TO_SECURITY_POLICY_EDIT';
export const GO_TO_SECURITY_POLICY_VIEW = 'GO_TO_SECURITY_POLICY_VIEW';
export const UPDATE_ERRORS = 'UPDATE_ERRORS';

export interface SecurityPolicy {
  name: string
  value: string
}

export interface SecurityPolicyAdaptedData {
  id: string
  minimumPasswordLength: string
  uniquePasswordHistoryCount: string
  passwordExpirationDays: string
  requireUppercase: string
  requireLowercase: string
  requireNumber: string
  requireSymbol: string
  allowNumericSequences: string
  maxConsecutiveCharacters: string
  allowPasswordUsernameMatch: string
  allowCurrentPasswordReuse: string
  enableAccountLockout: string
  maxAccountLoginAttempts: string
  accountLoginAttemptsIntervalMinutes: string
  accountLockDurationMinutes: string
  applyToSystemAdminAccount: string
  enableIpAddressBlocking: string
  maxIpAddressLoginAttempts: string
  ipAddressLoginAttemptsIntervalMinutes: string
  ipAddressBlockDurationMinutes: string
}

export const newSecurityPolicyAdaptedData: SecurityPolicyAdaptedData = {
  id: '',
  minimumPasswordLength: '',
  uniquePasswordHistoryCount: '',
  passwordExpirationDays: '',
  requireUppercase: '',
  requireLowercase: '',
  requireNumber: '',
  requireSymbol: '',
  allowNumericSequences: '',
  maxConsecutiveCharacters: '',
  allowPasswordUsernameMatch: '',
  allowCurrentPasswordReuse: '',
  enableAccountLockout: '',
  maxAccountLoginAttempts: '',
  accountLoginAttemptsIntervalMinutes: '',
  accountLockDurationMinutes: '',
  applyToSystemAdminAccount: '',
  enableIpAddressBlocking: '',
  maxIpAddressLoginAttempts: '',
  ipAddressLoginAttemptsIntervalMinutes: '',
  ipAddressBlockDurationMinutes: '',
};

export interface SecurityPolicySerialized {
  ACCESS: string
  GROUP: string
  IPADDR: string
  LBID: string
  OWNER: string
  id: string
}

export interface Password {
  id: string
  minimumPasswordLength: string
  uniquePasswordHistoryCount: string
  passwordExpirationDays: string
  requireUppercase: string
  requireLowercase: string
  requireNumber: string
  requireSymbol: string
  allowNumericSequences: string
  maxConsecutiveCharacters: string
  allowPasswordUsernameMatch: string
  allowCurrentPasswordReuse: string
}
export interface AccountLockout {
  id: string
  enableAccountLockout: string
  maxAccountLoginAttempts: string
  accountLoginAttemptsIntervalMinutes: string
  accountLockDurationMinutes: string
  applyToSystemAdminAccount: string
}
export interface IpAddressBlocking {
  id: string
  enableIpAddressBlocking: string
  maxIpAddressLoginAttempts: string
  ipAddressLoginAttemptsIntervalMinutes: string
  ipAddressBlockDurationMinutes: string
}

export interface SecurityPolicyFormData {
  passwordSettings: Password
  accountLockoutSettings: AccountLockout
  ipAddressBlockingSettings: IpAddressBlocking
}

export interface SecurityPolicyUiState {
  isLoading: boolean,
  wasSecurityPolicyUpdatedWithErrors: boolean,
}
export interface SecurityPolicyState {
  entities: SecurityPolicy[],
  errors: { securityPolicy: string },
  ui: SecurityPolicyUiState
}

export interface GetSecurityPolicyAction extends AnyAction {
  type: typeof GET_SECURITY_POLICY
  payload: AxiosPromise<ServerResponse<SecurityPolicy>>
}

export interface GetSecurityPolicyPendingAction extends AnyAction {
  type: typeof GET_SECURITY_POLICY_PENDING
  payload: AxiosResponse<ServerResponse<SecurityPolicy>>
}
export interface GetSecurityPolicyFulfilledAction extends AnyAction {
  type: typeof GET_SECURITY_POLICY_FULFILLED
  payload: AxiosResponse<ServerResponse<SecurityPolicy>>
}

export interface GetSecurityPolicyRejectedAction extends AnyAction {
  type: typeof GET_SECURITY_POLICY_REJECTED
  payload: AxiosResponse<ServerResponse<SecurityPolicy>>
}

export interface UpdateSecurityPolicyThunkAction extends ThunkAction<void, RootState, void, AnyAction> {}

export interface UpdateSecurityPolicyAction extends AnyAction {
  type: typeof UPDATE_SECURITY_POLICY
  payload: AxiosPromise<ServerResponse<SecurityPolicy>>
}

export interface UpdateSecurityPolicyPendingAction extends AnyAction {
  type: typeof UPDATE_SECURITY_POLICY_PENDING
  payload: AxiosResponse<ServerResponse<SecurityPolicy>>
}
export interface UpdateSecurityPolicyFulfilledAction extends AnyAction {
  type: typeof UPDATE_SECURITY_POLICY_FULFILLED
  payload: AxiosResponse<ServerResponse<SecurityPolicy>>
}

export interface UpdateSecurityPolicyRejectedAction extends AnyAction {
  type: typeof UPDATE_SECURITY_POLICY_REJECTED
  payload: AxiosResponse<ServerResponse<SecurityPolicy>>
}

export interface UpdateErrors extends AnyAction {
  type: typeof UPDATE_ERRORS
  payload: { securityPolicy: string }
}

export type SecurityPolicyActionTypes =
  GetSecurityPolicyAction |
  GetSecurityPolicyPendingAction |
  GetSecurityPolicyFulfilledAction |
  GetSecurityPolicyRejectedAction |
  UpdateSecurityPolicyAction |
  UpdateSecurityPolicyPendingAction |
  UpdateSecurityPolicyFulfilledAction |
  UpdateSecurityPolicyRejectedAction |
  UpdateErrors;
